import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils.js";

const textareaVariants = cva(
  "w-full rounded-md border bg-surface-0 shadow-sm transition-colors placeholder:text-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      size: {
        sm: "px-2.5 py-1.5 text-[13px]",
        md: "px-3 py-2 text-sm",
      },
      tone: {
        default: "border-surface-200 focus:border-brand-500",
        // --color-error-border sits outside the @theme block, so it has no
        // generated utility and is referenced as a variable.
        // Keeps its own border colour on focus -- pairing an error ring with a
        // brand border would read as two different states at once.
        error: "border-[var(--color-error-border)] focus:border-[var(--color-error-border)] focus:ring-error",
      },
      resize: {
        none: "resize-none",
        vertical: "resize-y",
      },
    },
    defaultVariants: { size: "md", tone: "default", resize: "none" },
  },
);

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size">,
    VariantProps<typeof textareaVariants> {
  /** Grows with the content between minRows and maxRows, then scrolls. */
  autoGrow?: boolean;
  minRows?: number;
  maxRows?: number;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, size, tone, resize, autoGrow = false, minRows = 1, maxRows = 6, value, onChange, ...props }, ref) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    const setRefs = React.useCallback((node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
    }, [ref]);

    // Measured, not static: the height depends on the text the user has typed
    // and the element's own line-height, which no Tailwind class can express.
    const fit = React.useCallback(() => {
      const el = innerRef.current;
      if (!el || !autoGrow) return;
      const styles = window.getComputedStyle(el);
      const lineHeight = parseFloat(styles.lineHeight) || 18;
      const padding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      const border = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
      el.style.height = "auto";
      const max = lineHeight * maxRows + padding + border;
      const min = lineHeight * minRows + padding + border;
      el.style.height = `${Math.min(Math.max(el.scrollHeight + border, min), max)}px`;
      el.style.overflowY = el.scrollHeight + border > max ? "auto" : "hidden";
    }, [autoGrow, maxRows, minRows]);

    React.useLayoutEffect(fit, [fit, value]);

    return (
      <textarea
        ref={setRefs}
        rows={minRows}
        value={value}
        onChange={(e) => { onChange?.(e); fit(); }}
        className={cn(textareaVariants({ size, tone, resize }), className)}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea, textareaVariants };
